import { defineStore } from 'pinia'
import { ref, computed, onUnmounted } from 'vue'
import type { Driver, CreateDriverRequest, UpdateDriverRequest } from '../types/driver'
import type { SearchFilters } from '../types/search'
import { driverService } from '../services/DriverService'


export const useDriverStore = defineStore('driver', () => {
  // State
  const drivers = ref<Driver[]>([])
  const selectedDriver = ref<Driver | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const searchFilters = ref<SearchFilters>({})


  // Getters
  const availableDrivers = computed(() => 
    drivers.value.filter(driver => driver.status === 'available')
  )

  const unavailableDrivers = computed(() => 
    drivers.value.filter(driver => driver.status === 'unavailable')
  )

  const driverCount = computed(() => drivers.value.length)

  const availableDriverCount = computed(() => availableDrivers.value.length)

  const filteredDrivers = computed(() => {
    if (!searchFilters.value.driverName && !searchFilters.value.driverId) {
      return drivers.value
    }

    return drivers.value.filter(driver => {
      const matchesName = !searchFilters.value.driverName || 
        driver.fullName.toLowerCase().includes(searchFilters.value.driverName.toLowerCase())
      
      const matchesId = !searchFilters.value.driverId || 
        driver.id === searchFilters.value.driverId

      return matchesName && matchesId
    })
  })

  // Actions
  const setLoading = (loading: boolean) => {
    isLoading.value = loading
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  const clearError = () => {
    error.value = null
  }

  const setDrivers = (driverList: Driver[]) => {
    drivers.value = driverList
  }

  const addDriver = (driver: Driver) => {
    drivers.value.unshift(driver) // Add to beginning for newest first
  }

  const updateDriverInList = (updatedDriver: Driver) => {
    const index = drivers.value.findIndex(d => d.id === updatedDriver.id)
    if (index !== -1) {
      drivers.value[index] = updatedDriver
    }
  }

  const removeDriverFromList = (driverId: string) => {
    const index = drivers.value.findIndex(d => d.id === driverId)
    if (index !== -1) {
      drivers.value.splice(index, 1)
    }
    // Clear selected driver if it was deleted
    if (selectedDriver.value?.id === driverId) {
      selectedDriver.value = null
    }
  }

  const setSelectedDriver = (driver: Driver | null) => {
    selectedDriver.value = driver
  }

  const setSearchFilters = (filters: SearchFilters) => {
    searchFilters.value = { ...filters }
  }

  const clearSearchFilters = () => {
    searchFilters.value = {}
  }

  // Service integration actions
  const fetchAllDrivers = async (): Promise<void> => {
    try {
      setLoading(true)
      clearError()
      const driverList = await driverService.getAllDrivers()
      setDrivers(driverList)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch drivers'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const fetchDriverById = async (id: string): Promise<Driver | null> => {
    try {
      setLoading(true)
      clearError()
      const driver = await driverService.getDriverById(id)
      return driver
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch driver'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const createDriver = async (driverData: CreateDriverRequest): Promise<Driver> => {
    try {
      setLoading(true)
      clearError()
      const newDriver = await driverService.createDriver(driverData)
      addDriver(newDriver)
      return newDriver
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create driver'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateDriver = async (id: string, driverData: UpdateDriverRequest): Promise<Driver> => {
    try {
      setLoading(true)
      clearError()
      const updatedDriver = await driverService.updateDriver(id, driverData)
      updateDriverInList(updatedDriver)
      
      // Update selected driver if it's the one being updated
      if (selectedDriver.value?.id === id) {
        setSelectedDriver(updatedDriver)
      }
      
      return updatedDriver
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update driver'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const deleteDriver = async (id: string): Promise<void> => {
    try {
      setLoading(true)
      clearError()
      await driverService.deleteDriver(id)
      removeDriverFromList(id)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete driver'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const searchDrivers = async (filters: SearchFilters): Promise<Driver[]> => {
    try {
      setLoading(true)
      clearError()
      const results = await driverService.searchDrivers(filters)
      setSearchFilters(filters)
      return results
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to search drivers'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const fetchDriversByStatus = async (status: 'available' | 'unavailable'): Promise<Driver[]> => {
    try {
      setLoading(true)
      clearError()
      const results = await driverService.getDriversByStatus(status)
      return results
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch drivers by status'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const fetchAvailableDrivers = async (): Promise<Driver[]> => {
    try {
      setLoading(true)
      clearError()
      const results = await driverService.getAvailableDrivers()
      return results
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch available drivers'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateDriverStatus = async (id: string, status: 'available' | 'unavailable'): Promise<Driver> => {
    try {
      setLoading(true)
      clearError()
      const updatedDriver = await driverService.updateDriverStatus(id, status)
      updateDriverInList(updatedDriver)
      
      // Update selected driver if it's the one being updated
      if (selectedDriver.value?.id === id) {
        setSelectedDriver(updatedDriver)
      }
      
      return updatedDriver
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update driver status'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const validatePhoneNumber = (phoneNumber: string): boolean => {
    return driverService.validatePhoneNumber(phoneNumber)
  }



  const transformDriverFromDatabase = (dbDriver: any): Driver => {
    return {
      id: dbDriver.id,
      fullName: dbDriver.full_name,
      phoneNumber: dbDriver.phone_number,
      status: dbDriver.status,
      createdAt: new Date(dbDriver.created_at),
      updatedAt: new Date(dbDriver.updated_at)
    }
  }

  // Cleanup on store destruction
  onUnmounted(() => {
    // Cleanup if needed
  })

  // Reset store state
  const resetStore = () => {
    drivers.value = []
    selectedDriver.value = null
    isLoading.value = false
    error.value = null
    searchFilters.value = {}
  }

  return {
    // State
    drivers,
    selectedDriver,
    isLoading,
    error,
    searchFilters,

    
    // Getters
    availableDrivers,
    unavailableDrivers,
    driverCount,
    availableDriverCount,
    filteredDrivers,
    
    // Actions
    setLoading,
    setError,
    clearError,
    setDrivers,
    addDriver,
    updateDriverInList,
    removeDriverFromList,
    setSelectedDriver,
    setSearchFilters,
    clearSearchFilters,
    
    // Service integration
    fetchAllDrivers,
    fetchDriverById,
    createDriver,
    updateDriver,
    deleteDriver,
    searchDrivers,
    fetchDriversByStatus,
    fetchAvailableDrivers,
    updateDriverStatus,
    validatePhoneNumber,
    

    
    // Utilities
    resetStore
  }
})